// Lib
import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import delay from '../../../../common/utils/lib/delay';

// Utils
import { animateScroll } from '../../../utils/animation/animateScrollIntoView';
import { getContentDomElement, getTargetScrollToAlignElementToEdge } from './scrollToElementUtils';
import { getSelectionRectWithoutWidth } from '../../../utils/dom/getSelectionRect';
import getParentScrollableSection from '../../../utils/dom/getParentScrollableSection';
import getTextSelectionFocusElement from '../../../components/editor/domUtils/getTextSelectionFocusElement';
import useObserveWindowSize from '../../../utils/dom/useObserveWindowSize';

// Selectors
import {
    getCurrentlyEditingEditorKey,
    getCurrentlyEditingId,
} from '../../../element/selection/currentlyEditingSelector';
import virtualKeyboardHeightSingleton from '../../../utils/keyboard/virtualKeyboardHeightSingleton';
import { DOCUMENT_MODAL_TITLE_EDITOR_KEY } from '../../../element/document/modal/documentModalConstants';
import { TABLE_GRID_EDITOR_KEY } from '../../../../common/table/tableConstants';

const TOOLBAR_TRANSITION_DELAY = 120;

type Props = {
    mobileToolbarRef?: React.RefObject<HTMLDivElement>;
};

const PREVENT_SCROLL_TO_ELEMENT_EDITOR_KEYS = [DOCUMENT_MODAL_TITLE_EDITOR_KEY, TABLE_GRID_EDITOR_KEY];

/**
 * Scrolls the scrollable parent (e.g. canvas or quick notes) to ensure that the caret
 * is visible when editing an element.
 */
const ScrollToCaretHandler = ({ mobileToolbarRef }: Props) => {
    const currentlyEditingId = useSelector(getCurrentlyEditingId);
    const currentlyEditingEditorKey = useSelector(getCurrentlyEditingEditorKey);
    const shouldPreventScroll = useRef<boolean>(false);

    const [, windowInnerHeight] = useObserveWindowSize();

    useEffect(() => {
        if (!currentlyEditingId) return;
        let animationCancelFn: (() => void) | undefined;

        const allowScrollToCaretOnStart = PREVENT_SCROLL_TO_ELEMENT_EDITOR_KEYS.includes(currentlyEditingEditorKey);

        // Skip scrolling to caret in the first second of starting editing.
        // This is because we are also animating scrolling to most elements in mobileEditingMiddleware.
        // Having 2 scroll animations causes screen to bobble on edit start
        shouldPreventScroll.current = true;
        setTimeout(() => {
            shouldPreventScroll.current = false;
        }, 1000);

        const onSelectionChange = () => {
            if (!currentlyEditingId) return;

            if (shouldPreventScroll.current) return;

            // TODO-MOBILE - For now only scroll if the selection is a caret. When it's a range it's much
            //  more difficult to determine the caret's position
            const selection = window.getSelection();
            if (selection?.type !== 'Caret') return;

            // NOTE: This isn't ideal as if the selection is not collapsed, it will be
            //  a range spanning the full selection.
            //  Ideally we'd want a range that is just the anchor position.
            const selectionRect = getSelectionRectWithoutWidth() as DOMRect;
            if (!selectionRect) return;

            const selectionDomElement = getTextSelectionFocusElement();
            const scrollableParent = getParentScrollableSection(selectionDomElement);
            if (!scrollableParent) return;

            const editedElement = getContentDomElement(currentlyEditingId, currentlyEditingEditorKey);
            if (!editedElement) return;

            const editedElementRect = editedElement.getBoundingClientRect();
            if (!editedElementRect) return;

            // WEB-11972 - Prevent jumps when the caret is scrolled out of view
            const scrollableParentRect = scrollableParent.getBoundingClientRect();
            if (selectionRect.top < scrollableParentRect.top) return;

            const bottomObstructionHeight = mobileToolbarRef?.current
                ? windowInnerHeight - mobileToolbarRef.current.getBoundingClientRect().top
                : virtualKeyboardHeightSingleton.getCurrentHeight();

            const { scrollTop: fromScrollTop, scrollLeft: fromScrollLeft } = scrollableParent;
            const { scrollTop: toScrollTop, scrollLeft: toScrollLeft } = getTargetScrollToAlignElementToEdge({
                scrollableParent,
                bottomObstructionHeight,
                scrollTargetRect: selectionRect,
            });

            if (fromScrollTop === toScrollTop && fromScrollLeft === toScrollLeft) return;

            if (animationCancelFn) animationCancelFn();

            animationCancelFn = animateScroll({
                scrollableParent,
                fromScrollLeft,
                fromScrollTop,
                toScrollLeft,
                toScrollTop,
                interpolationFactor: 0.1,
            });
        };

        // For elements that allow scroll to caret on start, we want to scroll when
        // the toolbar is in position so that we can calculate the scroll correctly
        const onKeyboardDidShow = () => {
            if (!allowScrollToCaretOnStart) return;

            shouldPreventScroll.current = false;
            delay(TOOLBAR_TRANSITION_DELAY).then(onSelectionChange);
        };

        document.addEventListener('selectionchange', onSelectionChange);
        window.addEventListener('keyboardDidShow', onKeyboardDidShow);

        return () => {
            shouldPreventScroll.current = false;
            document.removeEventListener('selectionchange', onSelectionChange);
            window.removeEventListener('keyboardDidShow', onKeyboardDidShow);
        };
    }, [currentlyEditingId, currentlyEditingEditorKey, windowInnerHeight]);

    return null;
};

export default ScrollToCaretHandler;
